// src/components/SecurityDashboard.js
import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../utils/firebase';

import { 
  getAllActiveGameSessions, 
  getUsersWithMultipleGames
} from '../utils/sessionManagement.js';

const SecurityDashboard = () => {
  const [securityEvents, setSecurityEvents] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [abuseData, setAbuseData] = useState({
    activeGameSessions: [],
    multipleGameUsers: [],
    flaggedUsers: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [eventsToShow, setEventsToShow] = useState(20);
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());
  const [alertsToShow, setAlertsToShow] = useState(20);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [showEventsSection, setShowEventsSection] = useState(false);
  const [showAlertsSection, setShowAlertsSection] = useState(false);
  const [showActiveSessions, setShowActiveSessions] = useState(false);
  const [showMultipleUsers, setShowMultipleUsers] = useState(false);
  const [showAccountProtection, setShowAccountProtection] = useState(false);

  useEffect(() => {
    loadSecurityData();
  }, [selectedTimeframe]);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      
      // Load recent security events
      let events = [];
      try {
        const eventsQuery = query(
          collection(db, 'securityEvents'),
          orderBy('timestamp', 'desc'),
          limit(50)
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        events = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.log('No security events found or collection does not exist yet');
      }
      setSecurityEvents(events);

      // Load user activity
      let activity = [];
      try {
        const activityQuery = query(
          collection(db, 'adminAlerts'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        const activitySnapshot = await getDocs(activityQuery);
        activity = activitySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.log('No admin alerts found or collection does not exist yet');
      }
      setUserActivity(activity);

      // Load anti-abuse data
      const activeGameSessions = getAllActiveGameSessions();
      const multipleGameUsers = getUsersWithMultipleGames();
      
      setAbuseData({
        activeGameSessions,
        multipleGameUsers,
        flaggedUsers: [] // Will be populated from security events
      });

    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const toggleEventExpansion = (eventId) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const toggleAlertExpansion = (alertId) => {
    const newExpanded = new Set(expandedAlerts);
    if (newExpanded.has(alertId)) {
      newExpanded.delete(alertId);
    } else {
      newExpanded.add(alertId);
    }
    setExpandedAlerts(newExpanded);
  };

  const handleSeverityClick = (severity) => {
    setSelectedSeverity(severity);
    setShowEventsSection(true);
    setShowAlertsSection(false);
  };

  const clearSeverityFilter = () => {
    setSelectedSeverity(null);
  };

  const getFilteredEvents = () => {
    if (!selectedSeverity) return securityEvents;
    return securityEvents.filter(event => event.severity === selectedSeverity);
  };

  const getFilteredEventsCount = (severity) => {
    return securityEvents.filter(event => event.severity === severity).length;
  };

  const renderCondensedAlert = (alert) => {
    const isExpanded = expandedAlerts.has(alert.id);
    const alertTypeDisplay = alert.eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const userDisplay = alert.userId ? `${alert.userId.substring(0, 8)}...` : 'Unknown';
    const timeDisplay = formatTimestamp(alert.createdAt);

    return (
      <div key={alert.id} className="border-b border-gray-700 last:border-b-0">
        {/* Condensed One-Line View */}
        <div 
          className="flex items-center justify-between p-3 hover:bg-gray-700 cursor-pointer transition-colors"
          onClick={() => toggleAlertExpansion(alert.id)}
        >
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <span className="text-lg">🚨</span>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium truncate">{alertTypeDisplay}</div>
              <div className="text-gray-400 text-sm truncate">
                {userDisplay} • {timeDisplay}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-900/20 text-yellow-400 border border-yellow-600">
              Pending
            </span>
            <span className="text-gray-400 text-sm">
              {isExpanded ? '▼' : '▶'}
            </span>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-3 pb-3 bg-gray-750 border-t border-gray-600">
            <div className="pt-3 space-y-2">
              <div className="text-sm text-gray-300">
                <span className="font-medium">Alert Type:</span> {alertTypeDisplay}
              </div>
              <div className="text-sm text-gray-300">
                <span className="font-medium">User ID:</span> {alert.userId || 'Unknown'}
              </div>
              <div className="text-sm text-gray-300">
                <span className="font-medium">Created:</span> {timeDisplay}
              </div>
              <div className="text-sm text-gray-300">
                <span className="font-medium">Status:</span> Pending Review
              </div>
              {alert.eventData && (
                <div className="text-sm text-gray-300">
                  <span className="font-medium">Alert Data:</span>
                  <pre className="mt-1 text-xs bg-gray-800 p-2 rounded overflow-x-auto">
                    {JSON.stringify(alert.eventData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCondensedEvent = (event) => {
    const isExpanded = expandedEvents.has(event.id);
    const eventTypeDisplay = event.eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const userDisplay = event.userId ? `${event.userId.substring(0, 8)}...` : 'System';
    const timeDisplay = formatTimestamp(event.timestamp);

    return (
      <div key={event.id} className="border-b border-gray-700 last:border-b-0">
        {/* Condensed One-Line View */}
        <div 
          className="flex items-center justify-between p-3 hover:bg-gray-700 cursor-pointer transition-colors"
          onClick={() => toggleEventExpansion(event.id)}
        >
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <span className="text-lg">{getEventIcon(event.eventType)}</span>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium truncate">{eventTypeDisplay}</div>
              <div className="text-gray-400 text-sm truncate">
                {userDisplay} • {timeDisplay}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(event.severity)}`}>
              {event.severity}
            </span>
            <span className="text-gray-400 text-sm">
              {isExpanded ? '▼' : '▶'}
            </span>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-3 pb-3 bg-gray-750 border-t border-gray-600">
            <div className="pt-3 space-y-2">
              <div className="text-sm text-gray-300">
                <span className="font-medium">Event Type:</span> {eventTypeDisplay}
              </div>
              <div className="text-sm text-gray-300">
                <span className="font-medium">User ID:</span> {event.userId || 'System Event'}
              </div>
              <div className="text-sm text-gray-300">
                <span className="font-medium">Timestamp:</span> {timeDisplay}
              </div>
              <div className="text-sm text-gray-300">
                <span className="font-medium">Severity:</span> {event.severity}
              </div>
              {event.eventData && (
                <div className="text-sm text-gray-300">
                  <span className="font-medium">Event Data:</span>
                  <pre className="mt-1 text-xs bg-gray-800 p-2 rounded overflow-x-auto">
                    {JSON.stringify(event.eventData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'multiple_failed_logins': return '🔐';
      case 'unusual_location_login': return '🌍';
              case 'rapid_purchases': return '💰';
        case 'large_purchase': return '💳';
      case 'suspicious_game_content': return '🎮';
      case 'payment_failed': return '❌';
      case 'payment_succeeded': return '✅';
      case '2fa_enabled': return '🔒';
      case '2fa_disabled': return '🔓';
      default: return '⚠️';
    }
  };

  const getSecurityStats = () => {
    const now = Date.now();
    const timeframeMs = selectedTimeframe === '24h' ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
    
    const recentEvents = securityEvents.filter(event => {
      const eventTime = event.timestamp?.toDate?.() || new Date(event.timestamp);
      return now - eventTime.getTime() < timeframeMs;
    });

    return {
      totalEvents: recentEvents.length,
      criticalEvents: recentEvents.filter(e => e.severity === 'critical').length,
      highEvents: recentEvents.filter(e => e.severity === 'high').length,
      mediumEvents: recentEvents.filter(e => e.severity === 'medium').length,
      lowEvents: recentEvents.filter(e => e.severity === 'low').length,
      pendingAlerts: userActivity.length
    };
  };

  const stats = getSecurityStats();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-800 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-800 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Controls */}
      <div className="flex justify-end items-center mb-6">
        <div className="flex space-x-4">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white border border-gray-600 rounded-lg px-4 py-2"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <button
            onClick={loadSecurityData}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Refresh
          </button>
        </div>
      </div>

        {/* Security Stats */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
          <button
            onClick={() => {
              setSelectedSeverity(null);
              setShowEventsSection(true);
              setShowAlertsSection(false);
            }}
            className={`bg-gray-800 rounded-lg p-6 border border-gray-700 transition-all duration-200 hover:scale-105 cursor-pointer ${
              showEventsSection && !selectedSeverity ? 'border-2 border-blue-400' : ''
            }`}
          >
            <div className="text-2xl font-bold text-white">{stats.totalEvents}</div>
            <div className="text-gray-400">Total Events</div>
            {showEventsSection && !selectedSeverity && (
              <div className="text-xs text-blue-300 mt-1">Viewing All</div>
            )}
          </button>
          <button
            onClick={() => handleSeverityClick('critical')}
            className={`rounded-lg p-6 transition-all duration-200 hover:scale-105 cursor-pointer ${
              selectedSeverity === 'critical' 
                ? 'bg-red-800 border-2 border-red-400' 
                : 'bg-red-900/20 border border-red-600 hover:bg-red-900/40'
            }`}
          >
            <div className="text-2xl font-bold text-red-400">{getFilteredEventsCount('critical')}</div>
            <div className="text-gray-400">Critical</div>
            {selectedSeverity === 'critical' && (
              <div className="text-xs text-red-300 mt-1">Filtered</div>
            )}
          </button>
          <button
            onClick={() => handleSeverityClick('high')}
            className={`rounded-lg p-6 transition-all duration-200 hover:scale-105 cursor-pointer ${
              selectedSeverity === 'high' 
                ? 'bg-orange-800 border-2 border-orange-400' 
                : 'bg-orange-900/20 border border-orange-600 hover:bg-orange-900/40'
            }`}
          >
            <div className="text-2xl font-bold text-orange-400">{getFilteredEventsCount('high')}</div>
            <div className="text-gray-400">High</div>
            {selectedSeverity === 'high' && (
              <div className="text-xs text-orange-300 mt-1">Filtered</div>
            )}
          </button>
          <button
            onClick={() => handleSeverityClick('medium')}
            className={`rounded-lg p-6 transition-all duration-200 hover:scale-105 cursor-pointer ${
              selectedSeverity === 'medium' 
                ? 'bg-yellow-800 border-2 border-yellow-400' 
                : 'bg-yellow-900/20 border border-yellow-600 hover:bg-yellow-900/40'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-400">{getFilteredEventsCount('medium')}</div>
            <div className="text-gray-400">Medium</div>
            {selectedSeverity === 'medium' && (
              <div className="text-xs text-yellow-300 mt-1">Filtered</div>
            )}
          </button>
          <button
            onClick={() => handleSeverityClick('low')}
            className={`rounded-lg p-6 transition-all duration-200 hover:scale-105 cursor-pointer ${
              selectedSeverity === 'low' 
                ? 'bg-green-800 border-2 border-green-400' 
                : 'bg-green-900/20 border border-green-600 hover:bg-green-900/40'
            }`}
          >
            <div className="text-2xl font-bold text-green-400">{getFilteredEventsCount('low')}</div>
            <div className="text-gray-400">Low</div>
            {selectedSeverity === 'low' && (
              <div className="text-xs text-green-300 mt-1">Filtered</div>
            )}
          </button>
          <button
            onClick={() => {
              setShowAlertsSection(!showAlertsSection);
              setShowEventsSection(false);
              clearSeverityFilter();
            }}
            className={`rounded-lg p-6 transition-all duration-200 hover:scale-105 cursor-pointer ${
              showAlertsSection 
                ? 'bg-yellow-800 border-2 border-yellow-400' 
                : 'bg-yellow-900/20 border border-yellow-600 hover:bg-yellow-900/40'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-400">{userActivity.length}</div>
            <div className="text-gray-400">Pending</div>
            {showAlertsSection && (
              <div className="text-xs text-yellow-300 mt-1">Viewing</div>
            )}
          </button>
        </div>

        {/* Expandable Events Section */}
        {showEventsSection && (
          <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-white">
                  Security Events
                  {selectedSeverity && (
                    <span className="ml-2 text-sm font-normal text-gray-400">
                      (Filtered: {selectedSeverity})
                    </span>
                  )}
                </h2>
                {selectedSeverity && (
                  <button
                    onClick={clearSeverityFilter}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
              <span className="text-sm text-gray-400">
                {getFilteredEvents().length} events
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {getFilteredEvents().length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">🛡️</div>
                  <p className="text-gray-400">
                    {selectedSeverity ? `No ${selectedSeverity} events found` : 'No security events found'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-0">
                    {getFilteredEvents().slice(0, eventsToShow).map(renderCondensedEvent)}
                  </div>
                  {getFilteredEvents().length > eventsToShow && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => setEventsToShow(eventsToShow + 20)}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm transition-colors"
                      >
                        Show More Events ({getFilteredEvents().length - eventsToShow} remaining)
                      </button>
                    </div>
                  )}
                  {eventsToShow > 20 && (
                    <div className="mt-2 text-center">
                      <button
                        onClick={() => setEventsToShow(20)}
                        className="text-gray-400 hover:text-white text-sm transition-colors"
                      >
                        Show Less
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Expandable Alerts Section */}
        {showAlertsSection && (
          <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Pending Alerts</h2>
              <span className="text-sm text-gray-400">
                {userActivity.length} alerts
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {userActivity.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">✅</div>
                  <p className="text-gray-400">No pending alerts</p>
                </div>
              ) : (
                <>
                  <div className="space-y-0">
                    {userActivity.slice(0, alertsToShow).map(renderCondensedAlert)}
                  </div>
                  {userActivity.length > alertsToShow && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => setAlertsToShow(alertsToShow + 20)}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm transition-colors"
                      >
                        Show More Alerts ({userActivity.length - alertsToShow} remaining)
                      </button>
                    </div>
                  )}
                  {alertsToShow > 20 && (
                    <div className="mt-2 text-center">
                      <button
                        onClick={() => setAlertsToShow(20)}
                        className="text-gray-400 hover:text-white text-sm transition-colors"
                      >
                        Show Less
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Anti-Abuse Monitoring */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-red-400 mb-4">🚨 Anti-Abuse Monitoring</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Active Game Sessions */}
            <button
              onClick={() => {
                setShowActiveSessions(!showActiveSessions);
                setShowMultipleUsers(false);
                setShowAccountProtection(false);
              }}
              className={`bg-gray-800 rounded-lg p-6 border border-gray-700 transition-all duration-200 hover:scale-105 cursor-pointer ${
                showActiveSessions ? 'border-2 border-blue-400' : ''
              }`}
            >
              <div className="text-3xl font-bold text-blue-400 mb-2">
                {abuseData.activeGameSessions.length}
              </div>
              <div className="text-gray-400 text-sm">Active Game Sessions</div>
              {showActiveSessions && (
                <div className="text-xs text-blue-300 mt-1">Viewing</div>
              )}
            </button>

            {/* Multiple Game Users */}
            <button
              onClick={() => {
                setShowMultipleUsers(!showMultipleUsers);
                setShowActiveSessions(false);
                setShowAccountProtection(false);
              }}
              className={`bg-gray-800 rounded-lg p-6 border border-gray-700 transition-all duration-200 hover:scale-105 cursor-pointer ${
                showMultipleUsers ? 'border-2 border-red-400' : ''
              }`}
            >
              <div className="text-3xl font-bold text-red-400 mb-2">
                {abuseData.multipleGameUsers.length}
              </div>
              <div className="text-gray-400 text-sm">Multiple Game Users</div>
              {showMultipleUsers && (
                <div className="text-xs text-red-300 mt-1">Viewing</div>
              )}
            </button>

            {/* Account Protection */}
            <button
              onClick={() => {
                setShowAccountProtection(!showAccountProtection);
                setShowActiveSessions(false);
                setShowMultipleUsers(false);
              }}
              className={`bg-gray-800 rounded-lg p-6 border border-gray-700 transition-all duration-200 hover:scale-105 cursor-pointer ${
                showAccountProtection ? 'border-2 border-green-400' : ''
              }`}
            >
              <div className="text-3xl font-bold text-green-400 mb-2">4</div>
              <div className="text-gray-400 text-sm">Protection Features</div>
              {showAccountProtection && (
                <div className="text-xs text-green-300 mt-1">Viewing</div>
              )}
            </button>
          </div>
        </div>

        {/* Expandable Active Sessions Section */}
        {showActiveSessions && (
          <div className="mt-6 bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Active Game Sessions</h3>
              <span className="text-sm text-gray-400">
                {abuseData.activeGameSessions.length} sessions
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {abuseData.activeGameSessions.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">🎮</div>
                  <p className="text-gray-400">No active game sessions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {abuseData.activeGameSessions.map((session, index) => (
                    <div key={index} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="text-white font-medium">Game: {session.gameId.substring(0, 8)}...</div>
                          <div className="text-gray-400 text-sm">User: {session.userId.substring(0, 8)}...</div>
                          <div className="text-gray-500 text-xs">Role: {session.userRole}</div>
                        </div>
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-900/20 text-blue-400 border border-blue-600">
                          Active
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Expandable Multiple Users Section */}
        {showMultipleUsers && (
          <div className="mt-6 bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Multiple Game Users</h3>
              <span className="text-sm text-gray-400">
                {abuseData.multipleGameUsers.length} users flagged
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {abuseData.multipleGameUsers.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">✅</div>
                  <p className="text-gray-400">No multiple game users detected</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {abuseData.multipleGameUsers.map((user, index) => (
                    <div key={index} className="bg-red-900/20 border border-red-600 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="text-red-400 font-semibold">User: {user.userId.substring(0, 8)}...</div>
                          <div className="text-gray-400 text-sm">{user.activeGames.length} active games</div>
                          <div className="text-gray-500 text-xs">Potential account sharing</div>
                        </div>
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-red-900/20 text-red-400 border border-red-600">
                          Flagged
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Expandable Account Protection Section */}
        {showAccountProtection && (
          <div className="mt-6 bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Account Protection Features</h3>
              <span className="text-sm text-gray-400">4 features active</span>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Single Session Limit</div>
                    <div className="text-gray-400 text-sm">Prevents multiple simultaneous sessions</div>
                  </div>
                  <span className="text-green-400 text-sm">✅ Active</span>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Single Game Limit</div>
                    <div className="text-gray-400 text-sm">Prevents multiple active games per user</div>
                  </div>
                  <span className="text-green-400 text-sm">✅ Active</span>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Device Tracking</div>
                    <div className="text-gray-400 text-sm">Monitors device fingerprinting</div>
                  </div>
                  <span className="text-green-400 text-sm">✅ Active</span>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">IP Monitoring</div>
                    <div className="text-gray-400 text-sm">Tracks IP address changes</div>
                  </div>
                  <span className="text-green-400 text-sm">✅ Active</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Recommendations */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Security Recommendations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Immediate Actions</h3>
              <ul className="space-y-2 text-gray-300">
                {stats.criticalEvents > 0 && (
                  <li className="flex items-center space-x-2">
                    <span className="text-red-400">⚠️</span>
                    <span>Review {stats.criticalEvents} critical security events</span>
                  </li>
                )}
                {stats.pendingAlerts > 0 && (
                  <li className="flex items-center space-x-2">
                    <span className="text-yellow-400">⚠️</span>
                    <span>Address {stats.pendingAlerts} pending alerts</span>
                  </li>
                )}
                {stats.highEvents > 5 && (
                  <li className="flex items-center space-x-2">
                    <span className="text-orange-400">⚠️</span>
                    <span>Investigate high volume of high-severity events</span>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Preventive Measures</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <span className="text-green-400">✅</span>
                  <span>Enable 2FA for all admin accounts</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-green-400">✅</span>
                  <span>Review user activity patterns</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-green-400">✅</span>
                  <span>Monitor payment transactions</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-green-400">✅</span>
                  <span>Update security policies</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SecurityDashboard; 